/**
 * @since 2026-04-19
 * @author Francesco Parrella
 * @maintainer Francesco Parrella
 * @copyright AlgoTraders, All rights reserved
 */

import { createContext, useContext } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./Notifications.scss";

const NotificationContext = createContext();

const recentNotifications = new Map();
const DUPLICATE_PREVENTION_TIME = 5000;

export const Notifications = ({ children }) => {
  const defaultOptions = {
    position: "top-right",
    autoClose: 3000,
    hideProgressBar: false,
    pauseOnHover: false,
    closeOnClick: true,
    rtl: false,
    newestOnTop: true,
  };

  const preventDuplicates = (message, type, options) => {
    const key = `${type}:${message}`;
    const now = Date.now();

    if (recentNotifications.has(key)) {
      const { timestamp, toastId } = recentNotifications.get(key);
      if (now - timestamp < DUPLICATE_PREVENTION_TIME) {
        toast.update(toastId, {
          ...options,
          render: message,
          autoClose: options.autoClose || defaultOptions.autoClose,
        });
        recentNotifications.set(key, { timestamp: now, toastId });
        return toastId;
      }
    }

    const toastId = toast[type](message, options);
    recentNotifications.set(key, { timestamp: now, toastId });

    setTimeout(() => {
      const entry = recentNotifications.get(key);
      if (entry && entry.timestamp === now) {
        recentNotifications.delete(key);
      }
    }, DUPLICATE_PREVENTION_TIME);

    return toastId;
  };

  const notifySuccess = (message, options = {}) =>
    preventDuplicates(message, "success", { ...defaultOptions, ...options });

  const notifyError = (message, options = {}) =>
    preventDuplicates(message, "error", { ...defaultOptions, ...options });

  const notifyInfo = (message, options = {}) =>
    preventDuplicates(message, "info", { ...defaultOptions, ...options });

  const notifyWarning = (message, options = {}) =>
    preventDuplicates(message, "warning", { ...defaultOptions, ...options });

  return (
    <NotificationContext.Provider
      value={{ notifySuccess, notifyError, notifyInfo, notifyWarning }}
    >
      {children}
      <ToastContainer />
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    const noop = () => {};
    return { notifySuccess: noop, notifyError: noop, notifyInfo: noop, notifyWarning: noop };
  }
  return ctx;
};

// Fire an OS-level notification when the tab is hidden. Lazy-requests permission
// on first call; silently no-ops if unsupported or denied. Visible tabs already
// show a toast so we skip the browser notification in that case to avoid dupes.
export const notifyBrowser = async (title, body, { tag } = {}) => {
  if (typeof window === "undefined" || !("Notification" in window)) return false;
  if (Notification.permission === "default") {
    try { await Notification.requestPermission(); } catch { return false; }
  }
  if (Notification.permission !== "granted") return false;
  if (typeof document !== "undefined" && !document.hidden) return false;
  try {
    new Notification(title, { body, tag });
    return true;
  } catch {
    return false;
  }
};

export default Notifications;
